import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Link } from "react-router-dom";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { ToastContainer, toast } from "react-toastify";
import getCurrentUserId from "../../../reduxSaga/sagas/requests/currentUserId";
import api from "../../../services/api";
import WarehouseService from "../../../services/WarehouseService";
import withLoader from "../../../components/layouts/WithLoader";

const animatedComponents = makeAnimated();

const formatSize = (bytes) =>
  bytes < 1024 * 1024
    ? `${(bytes / 1024).toFixed(1)} Ko`
    : `${(bytes / (1024 * 1024)).toFixed(2)} Mo`;

const isExcel = (file) => /\.(xlsx|xls)$/i.test(file?.name || "");

const UploadExcelProductSell = ({ startLoading, stopLoading }) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [notAdded, setNotAdded] = useState(null); // null = pas encore d'import

  useEffect(() => {
    WarehouseService.getAllRealWarehouseForPrice(getCurrentUserId(), 0)
      .then((response) => {
        if (response.data.success) {
          setWarehouses(response.data.success);
        } else {
          toast.error("Erreur : pas de dépôt");
        }
      })
      .catch((e) => {
        console.log("error in getAllRealWarehouse", e);
        toast.error("Erreur : impossible de charger les dépôts");
      });
  }, []);

  const warehouseOptions = useMemo(
    () =>
      warehouses
        .filter((w) => w?.id !== undefined)
        .map((w) => ({ value: w.id, label: w.name })),
    [warehouses]
  );

  const onTelechargerModel = () => {
    const worksheet = XLSX.utils.aoa_to_sheet([["Code à barre", "prix de vente", "stock"]]);
    worksheet["!cols"] = [{ wch: 20 }, { wch: 16 }, { wch: 10 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Model");
    const fileBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([fileBuffer], { type: "application/octet-stream" }), "model.xlsx");
  };

  const selectFile = (f) => {
    if (!f) return;
    if (!isExcel(f)) {
      toast.error("Format invalide : choisir un fichier .xlsx ou .xls");
      return;
    }
    setFile(f);
    setNotAdded(null);
  };

  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    selectFile(e.dataTransfer.files?.[0]);
  };

  const handleAjouter = () => {
    if (!selectedWarehouse) {
      toast.error("Veuillez choisir un dépôt");
      return;
    }
    if (!file) {
      toast.error("Aucun fichier sélectionné");
      return;
    }
    if (isUploading) return;

    const formData = new FormData();
    formData.append("file", file);
    setIsUploading(true);
    startLoading();
    api
      .post(
        `/retail/warehouse/addStockInitExcel/${getCurrentUserId()}/${selectedWarehouse.value}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data", Accept: "application/json" } }
      )
      .then((response) => {
        toast.success("Fichier téléchargé avec succès");
        setNotAdded(response.data["not addedd"] || []);
        clearFile();
      })
      .catch((error) => {
        console.error("Upload error", error);
        toast.error("Erreur lors du téléchargement du fichier");
      })
      .finally(() => {
        setIsUploading(false);
        stopLoading();
      });
  };

  return (
    <div className="content-wrapper" style={{ marginTop: "0" }}>
      <ToastContainer />
      <section className="content-header">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-6"></div>
            <div className="col-md-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <Link to="/" style={{ color: "black" }}>
                    Home
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/productSell" style={{ color: "black" }}>
                    Prix de vente
                  </Link>
                </li>
                <li className="breadcrumb-item active" style={{ color: "black" }}>
                  Import Excel
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="card card-dark">
            <div className="card-header d-flex align-items-center justify-content-between">
              <h3 className="card-title">
                <i className="fas fa-file-excel mr-2"></i>
                Importer les prix de vente depuis Excel
              </h3>
              <button className="btn btn-sm btn-light" onClick={onTelechargerModel}>
                <i className="fas fa-download mr-1"></i> Télécharger le modèle Excel
              </button>
            </div>

            <div className="card-body">
              <div className="callout callout-info">
                <h6 className="mb-1">Colonnes attendues</h6>
                <p className="mb-0">
                  <code>Code à barre</code>, <code>prix de vente</code>, <code>stock</code>.
                  Si la valeur d'une colonne n'est pas présente, mettre <code>-</code>.
                </p>
              </div>

              <div className="row">
                <div className="col-md-5">
                  <div className="form-group">
                    <label>1. Choisir le dépôt</label>
                    <Select
                      closeMenuOnSelect={true}
                      components={animatedComponents}
                      value={selectedWarehouse}
                      onChange={setSelectedWarehouse}
                      options={warehouseOptions}
                      placeholder="Choisir un dépôt..."
                      noOptionsMessage={() => "Aucun dépôt"}
                    />
                  </div>
                </div>
                <div className="col-md-7">
                  <div className="form-group">
                    <label>2. Choisir le fichier</label>
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                      }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={onDrop}
                      onClick={() => fileInputRef.current?.click()}
                      style={{
                        border: `2px dashed ${dragOver ? "#343a40" : "#adb5bd"}`,
                        background: dragOver ? "#f1f3f5" : "transparent",
                        borderRadius: 6,
                        padding: "18px 12px",
                        textAlign: "center",
                        cursor: "pointer",
                      }}
                    >
                      {file ? (
                        <div className="d-flex align-items-center justify-content-center" style={{ gap: 10 }}>
                          <i className="fas fa-file-excel text-success"></i>
                          <strong>{file.name}</strong>
                          <span className="text-muted">({formatSize(file.size)})</span>
                          <button
                            type="button"
                            className="btn btn-xs btn-outline-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              clearFile();
                            }}
                            aria-label="Retirer le fichier"
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ) : (
                        <span className="text-muted">
                          <i className="fas fa-cloud-upload-alt mr-2"></i>
                          Glisser un fichier .xlsx / .xls ici ou cliquer pour parcourir
                        </span>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx, .xls"
                        style={{ display: "none" }}
                        onChange={(e) => selectFile(e.target.files[0])}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card-footer text-right">
              <button
                className="btn btn-dark"
                onClick={handleAjouter}
                disabled={isUploading || !file || !selectedWarehouse}
              >
                <i className="fas fa-location-arrow mr-1" aria-hidden="true"></i>
                {isUploading ? "Import en cours..." : "Ajouter cette liste"}
              </button>
            </div>
          </div>

          {notAdded !== null &&
            (notAdded.length > 0 ? (
              <div className="card card-outline card-danger">
                <div className="card-header">
                  <h3 className="card-title">
                    Produits non ajoutés <span className="badge badge-danger">{notAdded.length}</span>
                  </h3>
                </div>
                <div className="card-body">
                  {notAdded.map((code, i) => (
                    <span key={i} className="badge badge-secondary mr-1 mb-1" style={{ fontSize: "0.85rem" }}>
                      {code}
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <div className="alert alert-success">Tous les produits ont été ajoutés.</div>
            ))}
        </div>
      </section>
    </div>
  );
};

export default withLoader(UploadExcelProductSell);
